import {runtimeStore} from '~/composables/store-accessor'
import {appInstance} from '~/utils/app-accessor'

import {axiosInstance} from '~/utils/axios-accessor'
import {orderFilters as blankOrderFilters} from '@/utils/store-blank-states'
import type {Ref} from 'vue'

export const usePersistentStore = defineStore('persistent', () => {
    const orderFilters: Ref = ref(blankOrderFilters())
    const ordersTableOptions = ref({
        itemsPerPage: 0,
        page: 1,
    })
    const clientsTableOptions = ref({
        itemsPerPage: 15,
        page: 1,
    })
    const selectedOrderId = ref(null)
    const selectedDestination = ref(null)
    const tourists: Ref<any[]> = ref([])
    const paymentMethods = ref([])
    const bookRequest = ref(null)
    const bookResponse = ref([
        {
            orderId: null,
            refNumber: null,
            status: null,
        },
    ])
    const paymentInitiateRequest = ref(null)
    const paymentInitiateResponse = ref({})
    const paymentCompleteRequest = ref(null)
    const paymentCompleteResponse = ref({})
    const conditions: Ref = ref({})
    const companySettings = ref({})
    const storageVersion = ref(null)
    const currency = ref('')
    const isItineraryView = ref(false)
    const selectedCompanyAndAgent = ref(null)
    const selectedPrivateClient = ref(null)
    const selectedTabIndex = ref(0)
    const commissionAndNettoCheckbox = ref(true)
    const showCancelledServices = ref(true)
    const showAccountBalance = ref(true)
    const cookieAlertShown = ref(false)
    const showConvertPrices = ref(true)

    const basketAddOns: Ref<any[]> = ref([])

    function SET_STORAGE_VERSION(val: any) {
        storageVersion.value = val
    }

    function SET_COOKIE_ALERT_SHOWN(shown: any) {
        cookieAlertShown.value = shown
    }

    function SET_COMMISSION_AND_NETTO_CHECKBOX(index: any) {
        commissionAndNettoCheckbox.value = index
    }

    function SET_SELECTED_TAB_INDEX(index: any) {
        selectedTabIndex.value = index
    }

    function SET_SHOW_CANCELLED_SERVICES(val: any) {
        showCancelledServices.value = val
    }

    function SET_SHOW_ACCOUNT_BALANCE(val: any) {
        showAccountBalance.value = val
    }

    function SET_SELECTED_COMPANY_AND_AGENT(companyAndAgent: any) {
        selectedPrivateClient.value = null
        selectedCompanyAndAgent.value = companyAndAgent
    }

    function SET_SELECTED_PRIVATE_CLIENT(client: any) {
        selectedCompanyAndAgent.value = null
        selectedPrivateClient.value = client
    }

    function SET_TOURIST_PROP({index, prop, val}: any) {
        tourists.value[index][prop] = val
    }

    function SET_TOURIST_PASSPORT_PROP({index, prop, val}: any) {
        tourists.value[index].passport[prop] = val
    }

    function SET_TOURIST({index, val}: any) {
        tourists.value[index] = val
    }

    function SET_TOURISTS(_tourists: any) {
        tourists.value = _tourists
    }

    function SET_PAYMENT_METHODS(_paymentMethods: any) {
        paymentMethods.value = _paymentMethods
    }

    function SET_BOOK_REQUEST(_bookRequest: any) {
        bookRequest.value = _bookRequest
    }

    function SET_BOOK_RESPONSE(_bookResponse: any) {
        bookResponse.value = _bookResponse
    }

    function SET_PAYMENT_INITIATE_REQUEST(_paymentInitiateRequest: any) {
        paymentInitiateRequest.value = _paymentInitiateRequest
    }

    function SET_PAYMENT_INITIATE_RESPONSE(_paymentInitiateResponse: any) {
        paymentInitiateResponse.value = _paymentInitiateResponse
    }

    function SET_PAYMENT_COMPLETE_REQUEST(_paymentCompleteRequest: any) {
        paymentCompleteRequest.value = _paymentCompleteRequest
    }

    function SET_PAYMENT_COMPLETE_RESPONSE(_paymentCompleteResponse: any) {
        paymentCompleteResponse.value = _paymentCompleteResponse
    }

    function SET_CONDITIONS(_conditions: any) {
        conditions.value[_conditions.offerKey] = _conditions
    }

    function REFRESH_CONDITIONS({offerKey, prepareBookResponse}: any) {
        //TODO Add new conditions from prepareBookResponse against return void
        if (!conditions.value[offerKey]) return
        conditions.value[offerKey].cancellationItems = prepareBookResponse.cancellations
    }

    function SET_ORDERS_TABLE_OPTIONS(val: any) {
        ordersTableOptions.value = val
    }

    function SET_CLIENTS_TABLE_OPTIONS(val: any) {
        clientsTableOptions.value = val
    }

    function SET_ORDER_FILTERS(val: any) {
        orderFilters.value = val
    }

    function SET_ORDER_FILTER({key, val}: any) {
        orderFilters.value[key] = val
    }

    function CLEAR_ORDER_FILTERS() {
        orderFilters.value = blankOrderFilters()
    }

    function SET_COMPANY_SETTINGS(settings: any) {
        companySettings.value = settings
    }

    function SET_CURRENCY(_currency: any) {
        currency.value = _currency
    }

    function SET_SELECTED_ORDER_ID(orderId: any) {
        selectedOrderId.value = orderId
    }

    function SET_SELECTED_DESTINATION(destination: any) {
        selectedDestination.value = destination
    }

    function SET_ITINERARY_VIEW(_isItineraryView: any) {
        isItineraryView.value = _isItineraryView
    }

    function ADD_BASKET_ADD_ONS({basketKey, selectedAddOns, offerKeys}: any) {
        if (basketAddOns.value.findIndex((basketAddOns: any) => basketAddOns.basketKey === basketKey) === -1) {
            basketAddOns.value.push({basketKey, selectedAddOns, offerKeys})
        }
    }

    function CLEAR_BASKET_ADD_ONS() {
        basketAddOns.value = []
    }

    async function getPaymentOptions(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        SET_PAYMENT_METHODS([])
        try {
            const rs = await appInstance.$api.paymentOptions.get(rq)
            if (rs.param && rs.param.paymentMethod && rs.param.paymentMethod.length) {
                SET_PAYMENT_METHODS(rs.param.paymentMethod)
            }
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    async function book(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            SET_BOOK_REQUEST(rq)
            const rs = await appInstance.$api.book.post(rq)
            SET_BOOK_RESPONSE(rs)
            return rs
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    async function paymentInitiate(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            SET_PAYMENT_INITIATE_REQUEST(rq)
            const rs = await appInstance.$api.onlinePaymentsInitiate.post(rq)
            SET_PAYMENT_INITIATE_RESPONSE(rs)
            return rs
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    async function paymentComplete(rq: any) {
        SET_PAYMENT_COMPLETE_REQUEST(rq)
        const rs = await appInstance.$api.onlinePaymentsComplete.post(rq)
        SET_PAYMENT_COMPLETE_RESPONSE(rs)
        return rs
    }

    async function loadConditions(rq: any) {
        const offerKey = rq.offerKey
        try {
            const cts = axiosInstance.CancelToken.source()
            runtimeStore.SET_CONDITIONS_LOADING({offerKey, cts})
            const rs = await appInstance.$api.conditions.get(rq, cts.token)
            if (rs) {
                rs.offerKey = offerKey
                SET_CONDITIONS(rs)
            }
        } catch (e: any) {
            if (e.status === 404) EventBus.$emit('OFFER_EXPIRED_EVENT')
            SET_CONDITIONS({offerKey, errors: ['Offer was expired']})
        } finally {
            runtimeStore.SET_CONDITIONS_LOADING({offerKey, cts: null})
        }
    }

    const getCurrency = computed(
        () => (originalCurrency: any) => (currency.value !== 'ORIGINAL' ? currency.value : originalCurrency)
    )

    return {
        orderFilters,
        ordersTableOptions,
        clientsTableOptions,
        selectedOrderId,
        selectedDestination,
        tourists,
        paymentMethods,
        bookRequest,
        bookResponse,
        paymentInitiateRequest,
        paymentInitiateResponse,
        paymentCompleteRequest,
        paymentCompleteResponse,
        conditions,
        companySettings,
        storageVersion,
        currency,
        isItineraryView,
        selectedCompanyAndAgent,
        selectedPrivateClient,
        selectedTabIndex,
        commissionAndNettoCheckbox,
        showCancelledServices,
        showAccountBalance,
        cookieAlertShown,
        basketAddOns,
        SET_STORAGE_VERSION,
        SET_COOKIE_ALERT_SHOWN,
        SET_COMMISSION_AND_NETTO_CHECKBOX,
        SET_SELECTED_TAB_INDEX,
        SET_SHOW_CANCELLED_SERVICES,
        SET_SHOW_ACCOUNT_BALANCE,
        SET_SELECTED_COMPANY_AND_AGENT,
        SET_SELECTED_PRIVATE_CLIENT,
        SET_TOURIST_PROP,
        SET_TOURIST_PASSPORT_PROP,
        SET_TOURIST,
        SET_TOURISTS,
        SET_PAYMENT_METHODS,
        SET_BOOK_REQUEST,
        SET_BOOK_RESPONSE,
        SET_PAYMENT_INITIATE_REQUEST,
        SET_PAYMENT_INITIATE_RESPONSE,
        SET_PAYMENT_COMPLETE_REQUEST,
        SET_PAYMENT_COMPLETE_RESPONSE,
        SET_CONDITIONS,
        REFRESH_CONDITIONS,
        SET_ORDERS_TABLE_OPTIONS,
        SET_CLIENTS_TABLE_OPTIONS,
        SET_ORDER_FILTERS,
        SET_ORDER_FILTER,
        CLEAR_ORDER_FILTERS,
        SET_COMPANY_SETTINGS,
        SET_CURRENCY,
        SET_SELECTED_ORDER_ID,
        SET_SELECTED_DESTINATION,
        SET_ITINERARY_VIEW,
        ADD_BASKET_ADD_ONS,
        CLEAR_BASKET_ADD_ONS,
        getPaymentOptions,
        book,
        paymentInitiate,
        paymentComplete,
        loadConditions,
        getCurrency,
        showConvertPrices,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(usePersistentStore, import.meta.hot))
